<template>
  <div
    :id="id"
    ref="choices"
    class="choices"
    :class="{
      'choices--expanded': expanded,
      'choices--has-expanded-label': hasExpandedLabel,
      'choices--contact-reason': contactReason,
      'choices--form-style': formStyle,
      'choices--font-normal': fontNormal,
    }"
    tabindex="0"
    @keydown.down.stop.prevent="handleArrowDown"
    @keydown.up.stop.prevent="handleArrowUp"
    @keydown.enter.stop.prevent="handleEnter"
    @keydown.space.stop.prevent="handleEnter"
    @keydown.esc.stop.prevent="handleEsc"
  >
    <div
      class="c-handle"
      aria-haspopup="listbox"
      aria-controls="dropdown-list"
      @click.prevent="handleHandleClick"
      @keydown.enter.stop.prevent="handleEnter"
      @keydown.space.stop.prevent="handleEnter"
    >
      <template v-if="!expanded || !hasExpandedLabel">
        <slot
          name="handle"
          :label="label"
          :selected-option="selectedOption"
        >
          <span
            v-if="showLabelForChoice"
            class="c-label"
          >
            {{ label }} {{ ' ' }}
          </span>
          <span
            v-if="!hideSelectedOptions"
            class="c-selected-item"
          >
            <template v-if="showSortingHeader">
              {{ $t('productListing.sortAfter') }}
            </template>
            <template v-else>
              {{ selectedOptionString }}
            </template>
          </span>
        </slot>
      </template>
      <template v-else>
        <slot name="expandedHandle" />
      </template>
    </div>

    <div
      v-show="expanded"
      ref="options"
      class="c-options"
      @keydown.tab.exact="expanded = false"
    >
      <component
        :is="optionElemType(option.value)"
        v-for="(option, index) in options"
        :id="generateOptionId(option.value)"
        :key="option.value ? option.value : option.label"
        :class="[
          'c-option',
          {
            'c-option--selected': isSelected(option.value),
            'c-option--active': index === activeOptionIndex,
            'c-option--disabled': option.disabled,
            'c-option--link': isOptionLinkEl(option.value),
          },
        ]"
        tabindex="0"
        :href="isOptionLinkEl(option.value) ? generateOptionId(option.value) : undefined"
        @click.prevent="handleItemClick(option.value)"
      >
        <slot v-bind="{ option, selected: isSelected(option.value), active: index === activeOptionIndex }">
          <div
            class="c-option-label"
            :class="{ 'c-option-parent-label': option.is_parent_category }"
          >
            <div v-html="option.label" />
            <fn-icon
              v-if="isSelected(option.value)"
              class="c-option--check"
              size="xxs"
              icon="checkmark-thin"
              color="black"
            />
          </div>
        </slot>
      </component>
    </div>
  </div>
</template>

<script lang="ts">
export default defineComponent({
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Array],
      default: '',
    },
    options: {
      required: true,
      type: Array as PropType<Array<{ label: string; value?: string | undefined }>>,
    },
    label: {
      type: String,
      default: '',
    },
    multiselect: {
      type: Boolean,
    },
    hideSelectedOptions: {
      type: Boolean,
      default: false,
    },
    showSortingHeader: {
      type: Boolean,
      default: false,
    },
    contactReason: {
      type: Boolean,
      default: false,
    },
    displayEmptyLabel: {
      type: Boolean,
      default: false,
    },
    formStyle: {
      type: Boolean,
      default: false,
    },
    idPrefix: {
      type: String,
      default: '',
    },
    fontNormal: {
      type: Boolean,
      default: false,
    },
    hideLabelWhenSelected: {
      type: Boolean,
      default: false,
    },
    optionsAsLinks: {
      type: Boolean,
    },
  },
  emits: ['update:value', 'checkboxChange'],
  data() {
    return {
      expanded: false,
      activeOptionIndex: -1, // Indicates the active item index for keyboard navigation
    };
  },
  computed: {
    selectedOption() {
      if (!this.multiselect) {
        return this.options.find((item) => this.value === item?.value);
      }

      return this.options.filter((item) => typeof this.value !== 'number' && this.value?.includes(item?.value));
    },

    selectedOptionString() {
      if (!this.multiselect) {
        if (this.selectedOption?.value === '' && !this.displayEmptyLabel) {
          return '';
        }

        return this.selectedOption?.label;
      }

      if (!this.selectedOption?.length) {
        return '';
      }

      const selectedOptions = [];

      for (let i = 0; i < this.selectedOption.length; i += 1) {
        selectedOptions.push(this.selectedOption[i as keyof typeof this.selectedOption].label);
      }

      return selectedOptions.join(', ');
    },

    firstSelectedOptionIndex() {
      if (!this.value) {
        return -1;
      }

      if (!this.multiselect) {
        return this.options.findIndex((item) => item.value === this.value);
      }

      return this.options.findIndex((item) => this.value.includes(item.value));
    },

    activeItem() {
      return this.options[this.activeOptionIndex];
    },

    hasExpandedLabel() {
      return !!this.$slots.expandedHandle;
    },

    showLabelForChoice() {
      if (this.value && this.hideLabelWhenSelected) {
        return false;
      }

      if (this.label) {
        return true;
      }

      return false;
    },
  },
  watch: {
    expanded(value: boolean) {
      if (value === false) {
        this.activeOptionIndex = -1;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleFocusOut.bind(this));
  },
  methods: {
    isSelected(value: any) {
      if (!this.value) {
        return false;
      }

      if (!this.multiselect) {
        return this.value === value;
      }

      return this.value.includes(value);
    },

    handleArrowDown() {
      if (this.activeOptionIndex + 1 >= this.options.length) {
        return;
      }

      this.activeOptionIndex += 1;
      this.scrollToActiveOption();
    },

    handleArrowUp() {
      if (this.activeOptionIndex <= 0) {
        return;
      }

      this.activeOptionIndex -= 1;
      this.scrollToActiveOption();
    },

    handleEnter() {
      if (this.expanded) {
        this.handleItemClick(this.activeItem.value);
      } else {
        this.activeOptionIndex = this.firstSelectedOptionIndex;
        this.expand();
      }
    },

    handleEsc() {
      this.expanded = false;
    },

    handleFocusOut(event: Event) {
      if (this.expanded && !this.$refs.choices?.contains(event.target)) {
        this.expanded = false;
      }
    },

    handleItemClick(value: any) {
      if (!this.multiselect) {
        this.emitValue(value);
        this.expanded = false;

        return;
      }

      const oldValues = (this.value || []) as Array<any>;
      const selectedChoices = (this.value || []) as Array<any>;

      if (oldValues.includes(value)) {
        const index = oldValues.findIndex((oldValue: any) => oldValue === value);
        oldValues.splice(index, 1);
        this.emitValue(oldValues);
        this.$emit('checkboxChange', { value, isChecked: false });
      } else {
        this.emitValue([...selectedChoices, value]);
        this.$emit('checkboxChange', { value, isChecked: true });
      }
    },

    handleHandleClick() {
      if (this.expanded) {
        this.expanded = false;
      } else {
        this.activeOptionIndex = this.firstSelectedOptionIndex;
        this.expand();
      }
    },

    emitValue(value: any) {
      this.$emit('update:value', value);
    },

    expand() {
      this.expanded = true;
      this.scrollToSelectedOption();
    },

    scrollToSelectedOption() {
      this.scrollToOptionIndex(this.firstSelectedOptionIndex);
    },

    scrollToActiveOption() {
      this.scrollToOptionIndex(this.activeOptionIndex);
    },

    scrollToOptionIndex(index) {
      if (!this.expanded) {
        return;
      }

      const selectedOption = this.$refs.options?.children[index];

      if (!selectedOption) {
        return;
      }

      this.$nextTick(() => {
        this.$refs.options?.scrollTo({ top: selectedOption.offsetTop });
      });
    },

    generateOptionId(value: any) {
      if (!value) {
        return null;
      }

      if (this.$host && this.isOptionLinkEl(value)) {
        return `${this.$host}${value}`;
      }

      return this.idPrefix ? `${this.idPrefix}.${value}` : value;
    },

    optionElemType(value: any) {
      return this.optionsAsLinks && !this.isSelected(value) ? 'a' : 'div';
    },

    isOptionLinkEl(value: any) {
      return this.optionElemType(value) === 'a';
    },
  },
});
</script>

<style lang="scss" scoped>
.choices {
  position: relative;
  display: block;
  user-select: none;
  cursor: pointer;
  border: 1px solid palette(grey, metallic);
  background-color: palette(grey, lighter);
  color: palette(black);
  border-radius: map-get($border-radiuses, 'xl');
  font-size: map-get($font-sizes, 'l');
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  .c-handle {
    display: block;
    padding: 0 $pad * 2.5 0 $pad;
    height: $ui-height - 2px;
    line-height: $ui-height - 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    // Triangle down
    &:after {
      content: '';
      display: block;
      position: absolute;
      right: $pad;
      top: 18px;
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 5px solid palette(black);
    }
  }

  .c-selected-item {
    pointer-events: none;
    font-weight: bold;
  }

  .c-label {
    pointer-events: none;
  }

  .c-options {
    position: absolute;
    white-space: nowrap;
    display: block;
    z-index: 4;
    left: -1px;
    right: -1px;
    top: $ui-height - 2px;
    max-height: 230px;
    overflow-x: hidden;
    overflow-y: scroll;
    background-color: palette(grey, lighter);
    border: 1px solid palette(grey, metallic);
    border-radius: 0 0 map-get($border-radiuses, 'xl') map-get($border-radiuses, 'xl');
    border-top: 0;
  }

  .c-option {
    transition: background-color 0.1s ease-in;
    min-width: 200px;

    &:hover {
      background-color: palette(sand);
    }

    &.c-option--selected.c-option--selected {
      background-color: palette(grey, light);
      font-weight: map-get($font-weights, 'bold');

      .c-option--check {
        padding: 0;
      }
    }

    &.c-option--active {
      background-color: palette(sand);
    }

    &.c-option--disabled {
      .c-option-label {
        display: inline-block;
      }

      .c-option-labelflag {
        font-size: map-get($font-sizes, 'm');
        font-weight: bold;
        word-break: break-word;
        color: palette(red);
      }
    }

    &.c-option--link {
      color: inherit;
      cursor: inherit;
      text-decoration: none;
      display: block;

      &:hover {
        color: inherit;
      }
    }
  }

  .c-option-label {
    width: 100%;
    padding: 0 $pad;
    line-height: $ui-height;
  }

  .c-option--selected .c-option-label {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  &:focus {
    border-color: palette(grey);
    outline: 0;
  }

  &:focus-visible {
    @include focus-outline-default;
  }

  &.choices--expanded {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    .c-handle {
      &:after {
        border-top: 0;
        border-bottom: 5px solid palette(black);
      }
    }

    &.choices--sorting {
      z-index: 2;
    }

    &.choices--has-expanded-label {
      .c-handle {
        padding: 0;
      }
    }
  }
}

.choices--contact-reason {
  .c-selected-item {
    font-weight: normal;
  }

  .c-options {
    z-index: 3;
    background-color: palette(sand, light);
  }

  .c-option {
    border-bottom: 1px solid palette(sand, base);

    &:first-child {
      display: none;
    }
  }

  .c-option-label {
    background-color: palette(sand, light);
    padding: 0 $pad * 2;
  }

  .c-option-parent-label {
    padding: 0 $pad;
    font-weight: bold;
  }
}

.choices--form-style {
  background-color: $color-general-background-neutral-default;

  .c-options {
    background-color: $color-general-background-neutral-default;
  }
}

.choices--font-normal {
  .c-selected-item {
    font-weight: normal;
  }
}
</style>
